/**
 * Groundwork Tasks v1 — create dialog.
 *
 * Opened from the global header "+ Task" (keyboard: T), from in-context
 * buttons on entity pages (pre-linked + title pre-filled), and from the field
 * app ("something for the office" — techs may only route to a queue).
 *
 * Tasks talk back (#1645): the asker says what "done" looks like, sees what
 * happens next (top of their list → they hand it back → you confirm), and
 * keeps the auto-bump on unless they turn it off. No texts.
 */
import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, Plus, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
    suggestVerbTitle,
    titleHasVerb,
    type DoneRule,
    type Task,
    type TaskLinkInput,
    type TaskType,
} from "@/lib/tasks";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { primaryActionLabel, softStopLine, stationForTopic, useBallSwitch, type Ball } from "@/lib/closeout-ball";
import { HandToDialog } from "@/components/closeout/handoff";

export interface TaskCreatePrefill {
    title?: string;
    links?: Array<TaskLinkInput & { label?: string }>;
}

interface UserOption {
    id: string;
    first_name: string;
    last_name: string;
    role_name: string;
    is_active: boolean;
}

export function TaskCreateDialog({
    open,
    onOpenChange,
    prefill,
}: {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    prefill?: TaskCreatePrefill;
}) {
    const qc = useQueryClient();
    const { isOffice } = useAuth();

    const [title, setTitle] = useState("");
    const [details, setDetails] = useState("");
    const [typeId, setTypeId] = useState<string>("");
    const [priority, setPriority] = useState<"normal" | "urgent">("normal");
    const [assigneeMode, setAssigneeMode] = useState<"queue" | "person">("queue");
    const [assigneeQueueId, setAssigneeQueueId] = useState<string>("");
    const [assigneeUserId, setAssigneeUserId] = useState<string>("");
    // Shared tasks (10/16): more people beside the first, and when it's done.
    const [alsoUserIds, setAlsoUserIds] = useState<string[]>([]);
    const [doneRule, setDoneRule] = useState<DoneRule>("anyone");
    const [dueDate, setDueDate] = useState<string>("");
    const [doneLooksLike, setDoneLooksLike] = useState<string>("");
    const [autoBump, setAutoBump] = useState<boolean>(true);
    // Who has the ball pick #3 — a SOFT stop: asking about a job Closeout
    // already tracks shows who has it first. Never a block: "Ask about
    // something else" opens the form as usual.
    const ballOn = useBallSwitch(open && isOffice).on;
    const navigate = useNavigate();
    const [askAnyway, setAskAnyway] = useState(false);
    const linkedJobId = (prefill?.links ?? []).find((l) => l.entityType === "job")?.entityId ?? null;
    const ballQ = useQuery({
        queryKey: ["close-out-ball-for-task", linkedJobId],
        queryFn: () =>
            api.get<{
                jobs: Array<{
                    id: string;
                    ball?: Ball | null;
                    site_name?: string | null;
                    company_name?: string | null;
                }>;
                approver?: { id: string; first_name: string } | null;
            }>(`/api/close-out/queue?jobId=${encodeURIComponent(linkedJobId ?? "")}`),
        enabled: open && isOffice && ballOn && !!linkedJobId,
        retry: false,
        staleTime: 30_000,
    });
    const trackedBall = ballQ.data?.jobs?.[0]?.ball ?? null;
    const softStop = open && isOffice && ballOn && !!linkedJobId && !askAnyway && !!trackedBall;
    // Hand to… (threads/hand-it-off): asking about a Closeout job offers
    // handing the JOB to a person first. Default person: whoever the job
    // waits on when that is one office person, else the approver.
    const queueJob = ballQ.data?.jobs?.[0] ?? null;
    const approver = ballQ.data?.approver ?? null;
    const [handTo, setHandTo] = useState<{ id: string | null } | null>(null);
    const handTarget: { id: string; name: string } | null = (() => {
        if (!trackedBall || trackedBall.handoff?.state === "open") return null;
        if (trackedBall.kind === "office" && trackedBall.userId && !trackedBall.mine) {
            return { id: trackedBall.userId, name: trackedBall.who };
        }
        return approver ? { id: approver.id, name: approver.first_name } : null;
    })();
    const handJobName = queueJob?.site_name || queueJob?.company_name || "this job";

    useEffect(() => {
        if (open) {
            setTitle(prefill?.title ?? "");
            setDetails("");
            setTypeId("");
            setPriority("normal");
            // A person first: asking a person is what closes the loop (#1645).
            setAssigneeMode(isOffice ? "person" : "queue");
            setAssigneeQueueId("");
            setAssigneeUserId("");
            setAlsoUserIds([]);
            setDoneRule("anyone");
            setDueDate("");
            setDoneLooksLike("");
            setAutoBump(true);
            setAskAnyway(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const typesQ = useQuery({
        queryKey: ["task-types"],
        queryFn: () => api.get<{ types: TaskType[] }>("/api/task-types"),
        enabled: open,
    });
    const queuesQ = useQuery({
        queryKey: ["task-queue-options"],
        queryFn: () => api.get<{ queues: Array<{ id: string; name: string }> }>("/api/task-queue-options"),
        enabled: open,
    });
    const usersQ = useQuery({
        queryKey: ["task-assignee-options"],
        queryFn: () => api.get<{ users: UserOption[] }>("/api/task-assignee-options"),
        enabled: open && isOffice,
    });

    const types = (typesQ.data?.types ?? []).filter((t) => t.isActive);
    const queues = queuesQ.data?.queues ?? [];
    const users = useMemo(() => (usersQ.data?.users ?? []).filter((u) => u.is_active), [usersQ.data]);

    function handleTypeChange(id: string) {
        setTypeId(id);
        const type = types.find((t) => t.id === id);
        // Type default queue routes the task unless a person was already chosen.
        if (type?.defaultQueueId && assigneeMode === "queue" && !assigneeQueueId) {
            setAssigneeQueueId(type.defaultQueueId);
        }
    }

    const createMut = useMutation({
        mutationFn: () =>
            api.post<{ task: Task }>("/api/tasks", {
                title: title.trim(),
                detailsRich: details.trim() || undefined,
                typeId: typeId || undefined,
                priority,
                assigneeUserId: assigneeMode === "person" ? assigneeUserId || undefined : undefined,
                assigneeQueueId: assigneeMode === "queue" ? assigneeQueueId || undefined : undefined,
                dueDate: dueDate || undefined,
                links: (prefill?.links ?? []).map((l) => ({ entityType: l.entityType, entityId: l.entityId })),
                doneLooksLike: doneLooksLike.trim() || undefined,
                autoBump,
                ...(sharing ? { alsoAssignUserIds: alsoPeople.map((u) => u.id), doneRule } : {}),
            }),
        onSuccess: () => {
            toast.success(
                assigneeMode === "person" && pickedUser
                    ? sharing
                        ? `Sent to ${alsoPeople.length + 1} people — follow it in Tasks → Sent by me`
                        : `Sent to ${pickedUser.first_name} — follow it in Tasks → Sent by me`
                    : "Task created",
            );
            qc.invalidateQueries({ queryKey: ["task-inbox"] });
            qc.invalidateQueries({ queryKey: ["task-triage"] });
            qc.invalidateQueries({ queryKey: ["tasks"] });
            onOpenChange(false);
        },
        onError: (e) => toast.error(e instanceof Error ? e.message : "Failed to create task"),
    });

    const pickedUser = users.find((u) => u.id === assigneeUserId) ?? null;
    const alsoPeople = alsoUserIds
        .filter((id) => id !== assigneeUserId)
        .map((id) => users.find((u) => u.id === id))
        .filter((u): u is UserOption => Boolean(u));
    const sharing = assigneeMode === "person" && !!pickedUser && alsoPeople.length > 0;

    function isoDate(d: Date): string {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    }
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(today.getDate() + 1);
    const friday = new Date(today);
    friday.setDate(today.getDate() + ((5 - today.getDay() + 7) % 7 || 7));
    const duePresets = [
        { label: "Today", value: isoDate(today) },
        { label: "Tomorrow", value: isoDate(tomorrow) },
        { label: "Friday", value: isoDate(friday) },
    ];

    const canSubmit =
        title.trim().length > 0 &&
        (assigneeMode === "queue" ? assigneeQueueId.length > 0 : assigneeUserId.length > 0) &&
        !createMut.isPending;

    return (
        <>
            {handTo && linkedJobId && (
                <HandToDialog
                    jobId={linkedJobId}
                    jobName={handJobName}
                    defaultToUserId={handTo.id}
                    onClose={() => setHandTo(null)}
                    onDone={() => onOpenChange(false)}
                />
            )}
            <Dialog open={open} onClose={() => onOpenChange(false)} title={isOffice ? "Ask someone" : "New task"}>
                {softStop && trackedBall ? (
                    <div className="space-y-3" data-testid="ball-soft-stop">
                        {trackedBall.handoff?.state === "open" ? (
                            <p className="rounded-lg bg-pink-50 px-3 py-2 text-[13px] font-semibold text-pink-900 dark:bg-pink-950/40 dark:text-pink-100">
                                This job is already with {trackedBall.handoff.toName}: “{trackedBall.handoff.reason}”
                            </p>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setHandTo({ id: handTarget?.id ?? null })}
                                className="flex w-full items-center gap-3 rounded-xl border-[1.5px] border-pink-600 bg-pink-50 px-3.5 py-3 text-left hover:bg-pink-100 dark:bg-pink-950/40 dark:hover:bg-pink-950/60"
                                data-testid="hand-instead"
                            >
                                <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-pink-700 text-white">
                                    →
                                </span>
                                <span>
                                    <span className="block text-sm font-extrabold text-pink-900 dark:text-pink-100">
                                        {handTarget
                                            ? `Hand this job to ${handTarget.name} instead`
                                            : "Hand this job to someone instead"}
                                    </span>
                                    <span className="text-[12px] text-muted-foreground">
                                        It goes to the top of their pile and comes back to you with the answer.
                                    </span>
                                </span>
                            </button>
                        )}
                        <p className="text-sm font-semibold">{softStopLine(trackedBall)}</p>
                        <p className="text-[12.5px] text-muted-foreground">{trackedBall.text}</p>
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button type="button" variant="outline" onClick={() => setAskAnyway(true)}>
                                Ask about something else
                            </Button>
                            <Button
                                type="button"
                                onClick={() => {
                                    onOpenChange(false);
                                    navigate(
                                        `/close-out?job=${linkedJobId}&station=${stationForTopic(trackedBall.topic)}`,
                                    );
                                }}
                            >
                                {primaryActionLabel(trackedBall)}
                            </Button>
                        </div>
                    </div>
                ) : (
                    <form
                        className="space-y-4"
                        onSubmit={(e) => {
                            e.preventDefault();
                            if (canSubmit) createMut.mutate();
                        }}
                    >
                        <div className="space-y-1.5">
                            <Label htmlFor="task-title">Title</Label>
                            <Input
                                id="task-title"
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                placeholder="Verb first — 'Fix…', 'Call…', 'Send…'"
                                maxLength={255}
                                autoFocus
                            />
                            {/* Title coaching (Work Inbox V2): if there's no verb, suggest one. */}
                            {title.trim().length > 3 && !titleHasVerb(title) && (
                                <p className="text-xs text-amber-700" data-testid="title-coach">
                                    Tip: {suggestVerbTitle(title)}
                                </p>
                            )}
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="task-done-looks-like">Done looks like</Label>
                            <Input
                                id="task-done-looks-like"
                                value={doneLooksLike}
                                onChange={(e) => setDoneLooksLike(e.target.value)}
                                placeholder="e.g. PO placed, and the ETA is in the comments"
                                maxLength={500}
                            />
                            <p className="text-xs text-muted-foreground">
                                They see this at the top, and it&apos;s what you check when it comes back.
                            </p>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="task-details">Details</Label>
                            <Textarea
                                id="task-details"
                                value={details}
                                onChange={(e) => setDetails(e.target.value)}
                                placeholder="Context, links, what done looks like…"
                                rows={4}
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <Label>Type</Label>
                                <Select value={typeId} onChange={(e) => handleTypeChange(e.target.value)}>
                                    <option value="">General</option>
                                    {types.map((t) => (
                                        <option key={t.id} value={t.id}>
                                            {t.name}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label>Priority</Label>
                                <div className="flex gap-1.5">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={priority === "normal" ? "default" : "outline"}
                                        className="flex-1"
                                        onClick={() => setPriority("normal")}
                                    >
                                        Normal
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={priority === "urgent" ? "destructive" : "outline"}
                                        className="flex-1"
                                        onClick={() => setPriority("urgent")}
                                    >
                                        Urgent
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label>Assign to</Label>
                            {isOffice && (
                                <div className="flex gap-1.5 mb-1.5">
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={assigneeMode === "queue" ? "default" : "outline"}
                                        onClick={() => setAssigneeMode("queue")}
                                    >
                                        Queue
                                    </Button>
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant={assigneeMode === "person" ? "default" : "outline"}
                                        onClick={() => setAssigneeMode("person")}
                                    >
                                        Person
                                    </Button>
                                </div>
                            )}
                            {assigneeMode === "queue" ? (
                                <Select value={assigneeQueueId} onChange={(e) => setAssigneeQueueId(e.target.value)}>
                                    <option value="">Pick a queue</option>
                                    {queues.map((q) => (
                                        <option key={q.id} value={q.id}>
                                            {q.name}
                                        </option>
                                    ))}
                                </Select>
                            ) : (
                                <Select value={assigneeUserId} onChange={(e) => setAssigneeUserId(e.target.value)}>
                                    <option value="">Pick a person</option>
                                    {users.map((u) => (
                                        <option key={u.id} value={u.id}>
                                            {u.first_name} {u.last_name}
                                            {u.role_name === "Technician" ? " (tech)" : ""}
                                        </option>
                                    ))}
                                </Select>
                            )}
                            {/* Shared tasks (10/16): more than one person on it. */}
                            {isOffice && assigneeMode === "person" && pickedUser && (
                                <div className="space-y-1.5 pt-1" data-testid="also-assign">
                                    {alsoPeople.length > 0 && (
                                        <div className="flex flex-wrap gap-1.5">
                                            {alsoPeople.map((u) => (
                                                <span
                                                    key={u.id}
                                                    className="inline-flex items-center gap-1 rounded-full border bg-card py-0.5 pl-2.5 pr-1 text-xs font-semibold"
                                                >
                                                    {u.first_name} {u.last_name}
                                                    <button
                                                        type="button"
                                                        aria-label={`Take ${u.first_name} off`}
                                                        onClick={() =>
                                                            setAlsoUserIds((ids) => ids.filter((id) => id !== u.id))
                                                        }
                                                        className="rounded-full p-0.5 hover:bg-secondary"
                                                    >
                                                        <X className="h-3 w-3" />
                                                    </button>
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                    <Select
                                        aria-label="Add another person"
                                        value=""
                                        onChange={(e) => {
                                            const id = e.target.value;
                                            if (id) setAlsoUserIds((ids) => (ids.includes(id) ? ids : [...ids, id]));
                                        }}
                                    >
                                        <option value="">+ Add another person</option>
                                        {users
                                            .filter((u) => u.id !== assigneeUserId && !alsoUserIds.includes(u.id))
                                            .map((u) => (
                                                <option key={u.id} value={u.id}>
                                                    {u.first_name} {u.last_name}
                                                    {u.role_name === "Technician" ? " (tech)" : ""}
                                                </option>
                                            ))}
                                    </Select>
                                </div>
                            )}
                        </div>

                        {sharing && (
                            <div className="space-y-1.5" data-testid="done-rule">
                                <Label>When is it done?</Label>
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {(
                                        [
                                            {
                                                id: "anyone",
                                                title: "When anyone finishes",
                                                line: "One person does it for the group.",
                                            },
                                            {
                                                id: "everyone",
                                                title: "When everyone finishes",
                                                line: "Each person checks off their part.",
                                            },
                                        ] as const
                                    ).map((o) => (
                                        <button
                                            key={o.id}
                                            type="button"
                                            aria-pressed={doneRule === o.id}
                                            onClick={() => setDoneRule(o.id)}
                                            className={cn(
                                                "rounded-lg border-2 px-3 py-2 text-left text-sm",
                                                doneRule === o.id ? "border-primary bg-primary/10" : "border-border",
                                            )}
                                        >
                                            <span className="block font-semibold">{o.title}</span>
                                            <span className="block text-xs text-muted-foreground">{o.line}</span>
                                        </button>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground">
                                    Either way you see a green check when it is done.
                                </p>
                            </div>
                        )}

                        <div className="space-y-1.5">
                            <Label htmlFor="task-due">By when? (optional)</Label>
                            <div className="flex flex-wrap items-center gap-1.5">
                                {duePresets.map((p) => (
                                    <Button
                                        key={p.label}
                                        type="button"
                                        size="sm"
                                        variant={dueDate === p.value ? "default" : "outline"}
                                        className="rounded-full"
                                        onClick={() => setDueDate(dueDate === p.value ? "" : p.value)}
                                    >
                                        {p.label}
                                    </Button>
                                ))}
                                <Input
                                    id="task-due"
                                    type="date"
                                    value={dueDate}
                                    onChange={(e) => setDueDate(e.target.value)}
                                    className="w-44"
                                />
                            </div>
                        </div>

                        {assigneeMode === "person" && (
                            <div className="space-y-2.5 rounded-lg bg-secondary p-3" data-testid="what-happens-next">
                                <p className="text-sm font-semibold">What happens next</p>
                                <div className="flex flex-wrap items-center gap-1.5 text-xs">
                                    <span className="rounded-full border bg-card px-2.5 py-1">
                                        Top of {pickedUser ? `${pickedUser.first_name}'s` : "their"} list
                                    </span>
                                    <span aria-hidden>→</span>
                                    <span className="rounded-full border bg-card px-2.5 py-1">
                                        {pickedUser ? pickedUser.first_name : "They"} hand it back
                                    </span>
                                    <span aria-hidden>→</span>
                                    <span className="rounded-full bg-emerald-100 px-2.5 py-1 font-semibold text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
                                        You see a green check ✓
                                    </span>
                                </div>
                                <label className="flex items-start gap-2 text-sm">
                                    <input
                                        type="checkbox"
                                        checked={autoBump}
                                        onChange={(e) => setAutoBump(e.target.checked)}
                                        className="mt-0.5 h-4 w-4 accent-[var(--color-primary)]"
                                    />
                                    <span>
                                        If it isn&apos;t opened within 2 working hours, bump it to the top of{" "}
                                        {pickedUser ? `${pickedUser.first_name}'s` : "their"} list
                                    </span>
                                </label>
                            </div>
                        )}

                        {(prefill?.links?.length ?? 0) > 0 && (
                            <div className="flex flex-wrap items-center gap-1.5">
                                {prefill!.links!.map((l) => (
                                    <span
                                        key={`${l.entityType}:${l.entityId}`}
                                        className="inline-flex items-center gap-1 rounded-full border bg-muted/60 px-2.5 py-0.5 text-xs"
                                    >
                                        <Link2 className="h-3 w-3" />
                                        {l.label ?? l.entityType}
                                    </span>
                                ))}
                            </div>
                        )}

                        <div className="flex justify-end gap-2 pt-1">
                            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" disabled={!canSubmit}>
                                {createMut.isPending
                                    ? "Sending…"
                                    : sharing
                                      ? `Send to ${alsoPeople.length + 1} people`
                                      : assigneeMode === "person" && pickedUser
                                        ? `Send to ${pickedUser.first_name}`
                                        : "Create task"}
                            </Button>
                        </div>
                    </form>
                )}
            </Dialog>
        </>
    );
}

/**
 * In-context "+ Task" button: pre-links the source entity and pre-fills the
 * title with context so the task carries a live link instead of pasted text.
 */
export function TaskCreateButton({
    prefill,
    size = "sm",
    variant = "outline",
    label = "+ Task",
    className,
}: {
    prefill?: TaskCreatePrefill;
    size?: "sm" | "default";
    variant?: "outline" | "ghost" | "default";
    label?: string;
    className?: string;
}) {
    const [open, setOpen] = useState(false);
    return (
        <>
            <Button size={size} variant={variant} className={className} onClick={() => setOpen(true)}>
                {label === "+ Task" ? (
                    <>
                        <Plus className="h-3.5 w-3.5" /> Task
                    </>
                ) : (
                    label
                )}
            </Button>
            <TaskCreateDialog open={open} onOpenChange={setOpen} prefill={prefill} />
        </>
    );
}

/**
 * Global header entry point: "+ Task" button plus the `T` keyboard shortcut
 * (skipped while typing in an input/textarea/contenteditable).
 */
export function GlobalTaskCreate() {
    const [open, setOpen] = useState(false);

    useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            if (e.key !== "t" && e.key !== "T") return;
            if (e.metaKey || e.ctrlKey || e.altKey) return;
            const el = document.activeElement as HTMLElement | null;
            if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return;
            e.preventDefault();
            setOpen(true);
        }
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, []);

    return (
        <>
            <Button size="sm" variant="outline" className="hidden md:inline-flex" onClick={() => setOpen(true)}>
                <Plus className="h-3.5 w-3.5" /> Task
            </Button>
            <TaskCreateDialog open={open} onOpenChange={setOpen} />
        </>
    );
}
